import React from "react";

import { Alert, Box, Stack, TextField, Typography } from "@mui/material";

import { useDrawerDirty } from "@shell/components/DrawerShell";
import { SelectCard } from "@shell/components/SelectCard";

import type { CreateCacheRequest } from "../../../types";

import { useCacheForm, type CacheFormState } from "./useCacheForm";

export interface CacheFormHandle {
  submit: () => boolean;
  getState: () => CacheFormState;
}

export interface CacheFormProps {
  onSubmit: (req: CreateCacheRequest) => void;
  isSubmitting: boolean;
}

export const CacheForm = React.forwardRef<CacheFormHandle, CacheFormProps>(
  function CacheForm({ onSubmit, isSubmitting }, ref): React.ReactElement {
    const { setIsDirty } = useDrawerDirty();
    const form = useCacheForm();

    const handleSubmit = () => {
      if (!form.validate()) return false;
      onSubmit(form.buildRequest());
      return true;
    };

    React.useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      getState: () => form.state,
    }));

    return (
      <Stack spacing={2.5}>
        <Alert
          severity="info"
          sx={{ "& .MuiAlert-message": { fontSize: "0.875rem" } }}
        >
          Redis is dedicated-only — each project gets its own instance for
          isolation and predictable performance.
        </Alert>

        {/* Name */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Cache Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="my_cache"
            value={form.state.name}
            onChange={(e) => {
              form.setField("name", e.target.value);
              setIsDirty(true);
            }}
            onBlur={() => form.onBlur("name")}
            error={!!form.errors.name}
            helperText={form.errors.name}
            disabled={isSubmitting}
          />
        </Box>

        {/* Persistence */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            Persistence
          </Typography>
          <Stack spacing={1}>
            <SelectCard
              label="None"
              description="In-memory only, no disk persistence"
              selected={form.state.persistence === "none"}
              onClick={() => {
                form.setField("persistence", "none");
                setIsDirty(true);
              }}
              disabled={isSubmitting}
            />
            <SelectCard
              label="AOF"
              description="Append-only file, durable writes"
              selected={form.state.persistence === "aof"}
              onClick={() => {
                form.setField("persistence", "aof");
                setIsDirty(true);
              }}
              disabled={isSubmitting}
            />
            <SelectCard
              label="RDB"
              description="Periodic snapshots, lower overhead"
              selected={form.state.persistence === "rdb"}
              onClick={() => {
                form.setField("persistence", "rdb");
                setIsDirty(true);
              }}
              disabled={isSubmitting}
            />
          </Stack>
        </Box>

        {/* Storage (hidden when persistence=none) */}
        {form.state.persistence !== "none" && (
          <Box sx={{ animation: "fadeIn 200ms ease" }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Storage Size (Gi)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.state.storageSizeGi}
              onChange={(e) => {
                form.setField("storageSizeGi", Number(e.target.value));
                setIsDirty(true);
              }}
              disabled={isSubmitting}
              inputProps={{ min: 1, max: 100 }}
            />
          </Box>
        )}

        {/* HA */}
        <Box>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            High Availability
          </Typography>
          <Stack spacing={1}>
            <SelectCard
              label="Single"
              description="One Redis instance"
              selected={form.state.ha === "single"}
              onClick={() => {
                form.setField("ha", "single");
                setIsDirty(true);
              }}
              disabled={isSubmitting}
            />
            <SelectCard
              label="Sentinel (3 nodes)"
              description="Redis Sentinel with automatic failover"
              selected={form.state.ha === "sentinel_3"}
              onClick={() => {
                form.setField("ha", "sentinel_3");
                setIsDirty(true);
              }}
              disabled={isSubmitting}
            />
          </Stack>
        </Box>

        <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      </Stack>
    );
  },
);

CacheForm.displayName = "CacheForm";
